import { ReactElement, Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fireEvent } from 'utils/generic/event-bus';
import { TAtoZMenuConfigs } from 'interfaces/generic';
import { AtoZMenuDefaultConfigs } from './constants';
import { ESearchElementEventBusActionTypes } from 'newelements/SearchElement/partials/types';
import { AtoZMenuContent } from 'components/AtoZMenuContent';
import { AtoZSkeleton } from 'components/AtoZMenuContent/Skeleton';
import { GlobalIcon } from 'components/GlobalIcon';
import { Popup } from 'components/Popup';
import { getHeaderState } from 'store/selectors/app-data';
import { getQuickSingleBetslipOpen } from 'store/selectors/betslip';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TAtoZMenuProps = {
  configs?: TAtoZMenuConfigs;
};

const atoZMenuDefaultProps = {
  configs: AtoZMenuDefaultConfigs
};

export const AtoZMenu = ({
  configs = AtoZMenuDefaultConfigs
}: TAtoZMenuProps = atoZMenuDefaultProps): ReactElement => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [isRtl] = useState(SpringConfigs.IS_RTL);
  const { isVisible: isHeaderVisible } = useSelector(getHeaderState);
  const isQuickSingleBetslipOpen = useSelector(getQuickSingleBetslipOpen);

  const chunkLoader = () => {
    import(
      /* webpackChunkName: "a-to-z-default-pages" */ 'components/AtoZMenuContent/DefaultPages'
    );
    import(
      /* webpackChunkName: "a-to-z-data-pages" */ 'components/AtoZMenuContent/DataPages'
    );
    import(
      /* webpackChunkName: "a-to-z-menu-content" */ 'components/AtoZMenuContent'
    );
    import(
      /* webpackChunkName: "language-switcher" */ 'components/Shared/LanguageSwitcher'
    );
    import(
      /* webpackChunkName: "sport-page" */ 'components/AtoZMenuContent/SportPage'
    );
    import(
      /* webpackChunkName: "a-to-z-sport-plain-view" */ 'components/AtoZMenuContent/SportListViews/PlainView'
    );
  };

  useEffect(() => {
    if (!location.hash) {
      if (open) {
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = 'visible';
      }
    }

    if (!open) {
      fireEvent({ key: ESearchElementEventBusActionTypes.RESET });
    }
  }, [open]);

  useEffect(() => {
    const openAToZ = () => {
      chunkLoader();
      setOpen(true);
    };

    window.openAZmenu = openAToZ;
  }, []);

  return (
    <>
      {window.fromNext ? (
        <div
          onClick={() => {
            chunkLoader();
            setOpen(true);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 32 32"
            fill="currentColor"
            className="x-svgInline v3-icon AtoZMenu__menu-burger-icon"
          >
            <path
              xmlns="http://www.w3.org/2000/svg"
              d="M6.85733 4.57031H25.144C25.7505 4.57031 26.3321 4.81123 26.7609 5.24006C27.1897 5.66889 27.4307 6.25052 27.4307 6.85698C27.4307 7.46344 27.1897 8.04506 26.7609 8.4739C26.3321 8.90273 25.7505 9.14365 25.144 9.14365L6.85733 9.14498C6.25123 9.14498 5.66994 8.9042 5.24136 8.47562C4.81278 8.04704 4.572 7.46575 4.572 6.85965C4.572 6.25354 4.81278 5.67225 5.24136 5.24367C5.66994 4.81509 6.25123 4.57431 6.85733 4.57431V4.57031ZM6.85733 22.8543H25.144C25.7505 22.8543 26.3321 23.0952 26.7609 23.5241C27.1897 23.9529 27.4307 24.5345 27.4307 25.141C27.4307 25.7474 27.1897 26.3291 26.7609 26.7579C26.3321 27.1867 25.7505 27.4276 25.144 27.4276H6.86C6.25389 27.4276 5.67261 27.1869 5.24403 26.7583C4.81544 26.3297 4.57467 25.7484 4.57467 25.1423C4.57467 24.5362 4.81544 23.9549 5.24403 23.5263C5.67261 23.0978 6.25389 22.857 6.86 22.857L6.85733 22.8543ZM2.28667 13.7116H29.7133C30.3198 13.7116 30.9014 13.9526 31.3303 14.3814C31.7591 14.8102 32 15.3919 32 15.9983C32 16.6048 31.7591 17.1864 31.3303 17.6152C30.9014 18.0441 30.3198 18.285 29.7133 18.285H2.28667C1.6802 18.285 1.09858 18.0441 0.669749 17.6152C0.240916 17.1864 0 16.6048 0 15.9983C0 15.3919 0.240916 14.8102 0.669749 14.3814C1.09858 13.9526 1.6802 13.7116 2.28667 13.7116V13.7116Z"
            ></path>
          </svg>
        </div>
      ) : (
        <GlobalIcon
          lib="generic"
          name="menuBurger"
          theme="default"
          size={EIconSize._24}
          className="AtoZMenu__menu-burger-icon"
          onClick={() => {
            chunkLoader();
            setOpen(true);
          }}
        />
      )}
      <Popup
        animationType={isRtl ? 'toLeft' : 'toRight'}
        className={cc([
          'burger-menu-drawer',
          {
            'burger-menu-drawer--withFixedHeader': isHeaderVisible,
            'overlay-index': isQuickSingleBetslipOpen
          }
        ])}
        posRight={isRtl ? '0' : '40px'}
        posLeft={isRtl ? '40px' : '0'}
        open={open}
        onMaskClick={() => setOpen(false)}
        mask
      >
        {open && (
          <div className="AtoZ-closeIconBlock">
            <GlobalIcon
              lib="generic"
              name="close"
              theme="default"
              size={EIconSize._18}
              onClick={() => setOpen(false)}
            />
          </div>
        )}
        {!visible && (
          <div className="a-z-loading">
            <AtoZSkeleton
              includeSearch
              sliderArrowsAToZ={configs.sliderArrowsAToZ}
            />
          </div>
        )}

        <Suspense fallback={null}>
          <AtoZMenuContent
            setVisible={setOpen}
            setOpen={setVisible}
            open={open}
            {...{ configs }}
          />
        </Suspense>
      </Popup>
    </>
  );
};
